import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Box, Typography, Grid } from '@mui/material'
import { capitalize } from 'lodash'
import { ReactComponent as RecurrenceIcon } from 'assets/Recurrence.svg'
import { EVENT_INSTANCE_TYPES } from 'settings/constants/service'
import {
  isWeeklyRecurrence as getIsWeeklyRecurrence,
  isNonWeeklyRecurrence as getIsNonWeeklyRecurrence,
  isDailyRecurrence,
} from 'utils/service'
import {
  selectEventInstanceType,
  selectEventOccurrence,
  selectDayOfTheWeek,
  selectDayOfTheMonth,
  selectRecurringServiceEvents,
  selectPricedServiceRecurrenceText,
  selectRecurrenceInterval,
} from 'data/configured-service/configuredServiceDetailsSelectors'
import T from 'T'
import ServiceDetailsViewItem from '../ServiceDetailsViewContent/ServiceDetailsViewItem'
import RecurrenceDetailsList from './RecurrenceDetailsList'

const RecurrenceDisplayList = ({ data, watchedRecurrence }) => {
  // Extract values from data using selectors
  const eventInstanceType = useMemo(() => selectEventInstanceType(data), [data])
  const eventOccurrence = useMemo(() => selectEventOccurrence(data), [data])
  const dayOfTheWeek = useMemo(() => selectDayOfTheWeek(data), [data])
  const dayOfTheMonth = useMemo(() => selectDayOfTheMonth(data), [data])
  const recurringServiceEvents = useMemo(() => selectRecurringServiceEvents(data), [data])
  const recurrenceText = useMemo(() => selectPricedServiceRecurrenceText(data), [data])
  const recurrenceInterval = useMemo(() => selectRecurrenceInterval(data), [data])

  // Use watchedRecurrence if provided, otherwise fall back to data-derived recurrenceInterval
  const effectiveRecurrenceInterval = watchedRecurrence?.recurrenceInterval || recurrenceInterval

  const isWeeklyRecurrence = effectiveRecurrenceInterval && getIsWeeklyRecurrence(effectiveRecurrenceInterval)
  const isNonWeeklyRecurrence = effectiveRecurrenceInterval && getIsNonWeeklyRecurrence(effectiveRecurrenceInterval)
  const isDailyInterval = effectiveRecurrenceInterval && isDailyRecurrence(effectiveRecurrenceInterval)

  return (
    <>
      {isWeeklyRecurrence && (
        <Box sx={{ width: '100%' }}>
          <ServiceDetailsViewItem icon={<RecurrenceIcon />} primary={recurrenceText} secondary={capitalize(T.REPEATS)} size="grow" />
        </Box>
      )}
      {isNonWeeklyRecurrence && (
        <Grid container size={12} flexWrap="nowrap" justifyContent="space-between" width="100%">
          <Grid>
            <ServiceDetailsViewItem
              icon={<RecurrenceIcon />}
              primary={recurrenceText}
              secondary={capitalize(T.REPEATS)}
              size="grow"
              sx={{
                '& > .MuiListItem-root': {
                  pl: 0,
                  ml: 0,
                },
              }}
            />
          </Grid>
          {!isDailyInterval && (
            <Grid>
              <Box>
                <Typography gutterBottom variant="subtitle1" fontWeight={500} textTransform="none" sx={{ mb: 1 }}>
                  {eventInstanceType === EVENT_INSTANCE_TYPES.DAY_OF_WEEK ? T.ON_THESE_DAYS : T.ON_THESE_DATES}:
                </Typography>
                <RecurrenceDetailsList
                  eventInstanceType={eventInstanceType}
                  eventOccurrence={eventOccurrence}
                  dayOfTheWeek={dayOfTheWeek}
                  dayOfTheMonth={dayOfTheMonth}
                  recurringServiceEvents={recurringServiceEvents}
                  recurrenceInterval={effectiveRecurrenceInterval}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      )}
    </>
  )
}

RecurrenceDisplayList.propTypes = {
  data: PropTypes.object.isRequired,
  watchedRecurrence: PropTypes.object,
}

export default RecurrenceDisplayList
